import { useState } from 'react'

import { useInteractionLock } from '@/context/InteractionLockContext'
import { useUAContext } from '@/context/UAContext'

/**
 * Encapsulates all device-aware interaction logic for world cards.
 *
 * - Guards pointer hover events against ghost touches on mobile browsers.
 * - Manages touch-based hover state using the InteractionLock mechanism.
 */
export function useCardInteraction(uniqueId: string) {
  const [isHovered, setIsHovered] = useState(false)

  const { tryAcquireLock, releaseLock } = useInteractionLock(uniqueId)

  const { isHoverSupported, isCoarsePointer } = useUAContext()

  // Avoid ghost mouse from touches on certain browsers on mobile devices
  // Side effect: without this when scrolling other cards may have focus which will takeover the lock
  const onPointerEnter = () => {
    if (isHoverSupported) setIsHovered(true)
  }

  const onPointerLeave = () => {
    if (isHoverSupported) setIsHovered(false)
  }

  // Standard way to prevent context menus during long press on touch devices (Android/iOS)
  // Allow normal right-click on desktop
  const onContextMenu = (e: React.MouseEvent) => {
    if (isCoarsePointer) e.preventDefault()
  }

  // Touch does NOT set isHovered — mobile video preview is handled by centre-line dwell.
  const onTouchStart = () => {
    tryAcquireLock()
  }

  const onTouchEnd = () => {
    releaseLock()
  }

  const onTouchCancel = () => {
    releaseLock()
  }

  return {
    isHovered,
    isHoverSupported,
    onPointerEnter,
    onPointerLeave,
    onContextMenu,
    onTouchStart,
    onTouchEnd,
    onTouchCancel,
  }
}
